import { useState, useEffect, useRef, ReactNode } from 'react';

import { TextField, CircularProgress } from '@mui/material';

import { Stack } from '@liscio/ui';

/**
 * Formats a number with commas for display (e.g., 5000.5 → "5,000.50").
 * Always shows up to 2 decimal places if there are cents, otherwise no decimals.
 */
const formatWithCommas = (value: number): string => {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

/**
 * Formats a raw input string with commas while preserving the decimal part as-is.
 * Handles partial inputs like "1234.", "1234.5", etc.
 */
const formatInputWithCommas = (value: string): string => {
  if (!value) return '';
  const parts = value.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.length > 1 ? integerPart + '.' + parts[1] : integerPart;
};

/**
 * Strips commas from a string for parsing (e.g., "5,000" → "5000").
 */
const stripCommas = (value: string): string => value.replace(/,/g, '');

/**
 * Counts significant characters (digits and decimal point) in a string.
 */
const countSignificantChars = (value: string): number =>
  value.replace(/[^0-9.]/g, '').length;

export interface EditableCurrencyFieldProps {
  /** Current amount in dollars */
  amountInDollars: number;
  /** Whether the field is editable */
  isEditable: boolean;
  /** Whether a save is currently in progress */
  isLoading: boolean;
  /** Called on blur when value has changed; must return a Promise that resolves on success and rejects on error */
  onSave: (amountInDollars: number) => Promise<void>;
  /** Called on every valid value change (fires synchronously on each keystroke) */
  onChange?: (amountInDollars: number) => void;
  /** Content to render when not editable */
  readOnlyDisplay: ReactNode;
  /** Minimum width as theme.spacing multiplier (default: 15) */
  minWidth?: number;
}

export const EditableCurrencyField = ({
  amountInDollars,
  isEditable,
  isLoading,
  onSave,
  onChange,
  readOnlyDisplay,
  minWidth = 15,
}: EditableCurrencyFieldProps) => {
  const [localAmount, setLocalAmount] = useState(amountInDollars);
  const [displayValue, setDisplayValue] = useState(
    formatWithCommas(amountInDollars)
  );
  const isFocusedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCursorRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sync local state when prop changes (e.g. after refetch).
  // Uses a ref for focus state to avoid re-running when focus changes.
  useEffect(() => {
    setLocalAmount(amountInDollars);
    if (!isFocusedRef.current) {
      setDisplayValue(formatWithCommas(amountInDollars));
    }
  }, [amountInDollars]);

  // Restore cursor position after React re-renders the formatted value
  useEffect(() => {
    if (pendingCursorRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(
        pendingCursorRef.current,
        pendingCursorRef.current
      );
      pendingCursorRef.current = null;
    }
  });

  const handleFocus = () => {
    isFocusedRef.current = true;
    setError(null);

    // Auto-select the value on focus so the user can immediately type a new amount
    // (especially helpful when the field contains "0")
    requestAnimationFrame(() => {
      inputRef.current?.select();
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPos = input.selectionStart || 0;

    // Count significant chars (digits + decimal) before cursor in the raw input
    const beforeCursor = rawValue.slice(0, cursorPos);
    const significantBeforeCursor = countSignificantChars(beforeCursor);

    // Strip everything except digits and decimal point
    let cleaned = rawValue.replace(/[^0-9.]/g, '');

    // Keep only the first decimal point
    const dotIndex = cleaned.indexOf('.');
    if (dotIndex !== -1) {
      cleaned =
        cleaned.slice(0, dotIndex + 1) +
        cleaned.slice(dotIndex + 1).replace(/\./g, '');
    }

    // Limit to 2 decimal places
    if (dotIndex !== -1 && cleaned.length - dotIndex - 1 > 2) {
      cleaned = cleaned.slice(0, dotIndex + 3);
    }

    // Format the cleaned value with commas
    const formatted = formatInputWithCommas(cleaned);

    // Calculate new cursor position: find the position in the formatted string
    // that has the same count of significant chars before it
    let sigCount = 0;
    let newCursorPos = formatted.length;
    for (let i = 0; i < formatted.length; i++) {
      if (/[0-9.]/.test(formatted[i])) {
        sigCount++;
      }
      if (sigCount === significantBeforeCursor) {
        newCursorPos = i + 1;
        break;
      }
    }

    setDisplayValue(formatted);
    pendingCursorRef.current = newCursorPos;

    // Notify parent of value change synchronously
    const parsed = parseFloat(stripCommas(cleaned));
    if (!isNaN(parsed) && parsed >= 0) {
      onChange?.(parsed);
    } else {
      // Reset parent to 0 for empty/invalid intermediate values so the parent
      // never holds a stale amount while the field is in an invalid state.
      onChange?.(0);
    }
  };

  const handleBlur = async () => {
    isFocusedRef.current = false;

    const rawValue = stripCommas(displayValue.trim());
    const numValue = parseFloat(rawValue);

    // Validate
    if (rawValue === '' || isNaN(numValue)) {
      setError('Not a valid amount');
      setDisplayValue(formatWithCommas(localAmount));
      return;
    }
    if (numValue < 0) {
      setError('Amount cannot be negative');
      setDisplayValue(formatWithCommas(localAmount));
      return;
    }

    setError(null);
    setDisplayValue(formatWithCommas(numValue));

    // Skip API call if value hasn't changed
    if (numValue === localAmount) return;

    try {
      await onSave(numValue);
      setLocalAmount(numValue);
    } catch {
      // Revert to the last confirmed value on error
      setDisplayValue(formatWithCommas(localAmount));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  if (!isEditable) {
    return <>{readOnlyDisplay}</>;
  }

  return (
    <Stack
      sx={(theme) => ({
        minWidth: theme.spacing(minWidth),
        position: 'relative',
      })}
    >
      <TextField
        value={displayValue}
        onChange={handleChange}
        error={Boolean(error)}
        helperText={error}
        size="small"
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
        inputRef={inputRef}
        aria-label="Currency amount"
        sx={(theme) => ({
          '& .MuiFormHelperText-root': {
            marginTop: 0,
            marginLeft: 0,
            marginRight: 0,
            position: 'absolute',
            bottom: theme.spacing(-2.5),
            fontSize: theme.typography.caption.fontSize,
          },
          '& .MuiInputBase-input': {
            textAlign: 'right',
            padding: theme.spacing(1),
          },
        })}
        inputProps={{
          inputMode: 'decimal',
        }}
      />
      {isLoading && (
        <CircularProgress
          size={16}
          sx={(theme) => ({
            position: 'absolute',
            right: theme.spacing(1),
            top: 0,
            bottom: 0,
            margin: 'auto',
          })}
        />
      )}
    </Stack>
  );
};
